import React, { JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  FaceSmileIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { useAuth } from 'context/user'
import { useEntitlements, planDisplayName } from 'context/plan'
import { useTwitchConnection } from 'hooks/useTwitchConnection'
import { AccountOverlayLinkState, useAccountOverlayLink } from 'hooks/useAccountOverlayLink'
import { LoadingSpinner } from 'components/Loading'
import { NumberInput } from 'components/NumberInput'
import { ErrorInline } from 'components/ErrorInline'
import { SavedNote } from 'components/SavedNote'
import { ProviderBrandIcon, TWITCH_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { graphqlRequest } from 'lib/helpers'
import { sortByName } from 'lib/elementOrder'
import { EmoteWallMockup } from 'components/overlayPreview/OverlayMockups'
import { usePrefersReducedMotion } from 'components/overlayPreview/previewFeeds'
import { DeletionResult, EmoteWallSummary } from 'types/api'
import {
  SectionCard,
  FieldLabel,
  OVERLAY_LINK_NOTE,
  OverlayLinkBlock,
  OverlayLinkRowActions,
  QuickOverlayManagerView,
  ownUnlistedLogin
} from 'components/chatOverlays/ChatOverlayManager'
import createEmoteWallMutation from 'graphql/createEmoteWall.graphql'
import updateEmoteWallMutation from 'graphql/updateEmoteWall.graphql'
import deleteEmoteWallMutation from 'graphql/deleteEmoteWall.graphql'
import listMyEmoteWallsQuery from 'graphql/listMyEmoteWalls.graphql'
import duplicateEmoteWallMutation from 'graphql/duplicateEmoteWall.graphql'

const FALLBACK_MAX_CHANNELS = 3
const MIN_EMOTE_SIZE = 16
const MAX_EMOTE_SIZE = 256
const MIN_DENSITY = 1
const MAX_DENSITY = 100
const MIN_DURATION_SECONDS = 2
const MAX_DURATION_SECONDS = 30

type EmoteWallStyle = 'RAIN' | 'POP'

const normalizeStyle = (raw: string): EmoteWallStyle =>
  raw === 'POP' ? 'POP' : 'RAIN'

const styleLabel = (raw: string): string =>
  normalizeStyle(raw) === 'POP' ? 'Pop' : 'Rain'

export interface EmoteWallDraft {
  name: string
  twitchChatChannels: string[]
  style: EmoteWallStyle
  emoteSize: number
  density: number
  durationSeconds: number
  showTwitchEmotes: boolean
  show7tvEmotes: boolean
  showBttvEmotes: boolean
  showFfzEmotes: boolean
}

type NumericWallField = {
  [K in keyof EmoteWallDraft]: EmoteWallDraft[K] extends number ? K : never
}[keyof EmoteWallDraft]

type ProviderToggleField = {
  [K in keyof EmoteWallDraft]: EmoteWallDraft[K] extends boolean ? K : never
}[keyof EmoteWallDraft]

const emptyDraft = (): EmoteWallDraft => ({
  name: '',
  twitchChatChannels: [],
  style: 'RAIN',
  emoteSize: 56,
  density: 40,
  durationSeconds: 8,
  showTwitchEmotes: true,
  show7tvEmotes: true,
  showBttvEmotes: true,
  showFfzEmotes: true
})

const draftFromWall = (wall: EmoteWallSummary): EmoteWallDraft => ({
  name: wall.name,
  twitchChatChannels: [...wall.twitchChatChannels],
  style: normalizeStyle(wall.style),
  emoteSize: wall.emoteSize,
  density: wall.density,
  durationSeconds: wall.durationSeconds,
  showTwitchEmotes: wall.showTwitchEmotes,
  show7tvEmotes: wall.show7tvEmotes,
  showBttvEmotes: wall.showBttvEmotes,
  showFfzEmotes: wall.showFfzEmotes
})

const normalizeLogin = (raw: string): string =>
  raw.trim().toLowerCase().replace(/^#/, '')

const WALL_STYLE_TOGGLES: Array<{ style: EmoteWallStyle, label: string, testId: string }> = [
  { style: 'RAIN', label: 'Rain', testId: 'wall-style-rain' },
  { style: 'POP', label: 'Pop', testId: 'wall-style-pop' }
]

const EmoteWallForm = ({
  existing,
  auth0Token,
  overlayLink,
  onSaved,
  onCancel,
  backLabel
}: {
  existing: EmoteWallSummary | null
  auth0Token: string | undefined
  overlayLink: AccountOverlayLinkState
  onSaved: (wall: EmoteWallSummary) => void
  onCancel: () => void
  backLabel: string
}): JSX.Element => {
  const isEdit = existing !== null
  const [draft, setDraft] = useState<EmoteWallDraft>(
    existing === null ? emptyDraft() : draftFromWall(existing)
  )
  const [channelInput, setChannelInput] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState(false)
  useEffect(() => {
    setSavedNote(false)
  }, [draft])
  const previewReducedMotion = usePrefersReducedMotion(true)

  const { status: planStatus, limitFor, upgradeForLimit } = useEntitlements()
  const channelCap = planStatus === 'ready'
    ? limitFor('twitchChannelsPerOverlay')
    : FALLBACK_MAX_CHANNELS
  const atChannelCap = channelCap !== null && draft.twitchChatChannels.length >= channelCap
  const channelUpgrade = atChannelCap
    ? upgradeForLimit('twitchChannelsPerOverlay', channelCap)
    : null

  const { login: myTwitchLogin } = useTwitchConnection()
  const quickAddLogin = atChannelCap
    ? null
    : ownUnlistedLogin(myTwitchLogin, draft.twitchChatChannels)

  const removeChannel = (login: string): void => {
    setDraft((d) => ({
      ...d,
      twitchChatChannels: d.twitchChatChannels.filter((c) => c !== login)
    }))
  }

  const addChannels = (): void => {
    const fresh = channelInput
      .split(/[\s,]+/)
      .map(normalizeLogin)
      .filter((c) => c !== '' && /^[a-z0-9_]{1,25}$/.test(c))
    if (fresh.length === 0) return
    setDraft((d) => {
      const merged = [...new Set([...d.twitchChatChannels, ...fresh])]
      return {
        ...d,
        twitchChatChannels: channelCap === null ? merged : merged.slice(0, channelCap)
      }
    })
    setChannelInput('')
  }

  const commitInt = (field: NumericWallField) => (n: number): void => {
    setDraft((d) => ({ ...d, [field]: n }))
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (auth0Token === undefined || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const variables = { ...draft, name: draft.name.trim() }
      const { wall } = existing === null
        ? await graphqlRequest<{ wall: EmoteWallSummary }>(
          createEmoteWallMutation, variables, auth0Token
        )
        : await graphqlRequest<{ wall: EmoteWallSummary }>(
          updateEmoteWallMutation, { emoteWallId: existing.emoteWallId, ...variables }, auth0Token
        )
      setSubmitting(false)
      setSavedNote(true)
      onSaved(wall)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save emote wall')
      setSubmitting(false)
    }
  }

  const draftValid = draft.name.trim() !== '' && draft.twitchChatChannels.length > 0
  const inputClass = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'

  const formTitle = isEdit ? 'Edit emote wall' : 'New emote wall'

  const providerToggles: Array<{ field: ProviderToggleField, label: string }> = [
    { field: 'showTwitchEmotes', label: 'Twitch emotes (native, from the message itself)' },
    { field: 'show7tvEmotes', label: '7TV emotes (global + each channel’s set)' },
    { field: 'showBttvEmotes', label: 'BTTV emotes (global + each channel’s set)' },
    { field: 'showFfzEmotes', label: 'FFZ emotes (global + each channel’s set)' }
  ]

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
        }
      }}
      className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3'
    >
      <div className='space-y-1.5 lg:col-span-2'>
        <fieldset className='contents space-y-1.5'>
          <SectionCard
            title={formTitle}
            action={
              <button
                type='button'
                onClick={onCancel}
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
              >
                <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                {backLabel}
              </button>
            }
          >
            <label className='mb-4 block'>
              <FieldLabel>Name</FieldLabel>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder='Main emote wall, Hype wall, …'
                required
                maxLength={80}
                className={inputClass}
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                An identifier, not displayed on the overlay.
              </p>
            </label>

            <div>
              <FieldLabel>
                <span className='flex items-center gap-1.5'>
                  <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                  Twitch channels
                </span>
              </FieldLabel>
              {quickAddLogin !== null && (
                <button
                  type='button'
                  onClick={() => {
                    setDraft((d) => ({
                      ...d,
                      twitchChatChannels: [...new Set([...d.twitchChatChannels, quickAddLogin])]
                    }))
                  }}
                  data-testid='own-channel-quick-add'
                  className='mb-2 inline-flex items-center gap-1.5 rounded-md border border-brand/20 bg-brand/5 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:bg-brand/10'
                >
                  <PlusIcon className='size-3.5 shrink-0 text-brand' aria-hidden='true' />
                  Add your channel:{' '}
                  <span className='font-semibold text-dm-text-primary'>{quickAddLogin}</span>
                </button>
              )}
              <div className='flex gap-1.5'>
                <input
                  type='text'
                  value={channelInput}
                  onChange={(e) => setChannelInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    addChannels()
                  }}
                  disabled={atChannelCap}
                  placeholder='any_channel, another_channel'
                  autoComplete='off'
                  className='min-w-0 flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60'
                />
                <button
                  type='button'
                  onClick={addChannels}
                  disabled={atChannelCap}
                  className='shrink-0 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                >
                  Add
                </button>
              </div>
              <ul className='mt-2 space-y-1.5'>
                {draft.twitchChatChannels.map((login) => (
                  <li key={login}>
                    <label className='flex cursor-pointer items-center gap-2.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-sm hover:bg-dm-bg-accent/50'>
                      <input
                        type='checkbox'
                        checked
                        onChange={() => removeChannel(login)}
                        className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                      />
                      <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                      <span className='text-dm-text-primary'>{login}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                {channelCap === null
                  ? <>Unlimited channels per overlay ({draft.twitchChatChannels.length} added). Use comma or space to separate, then add.</>
                  : <>Up to {channelCap} channels per overlay ({draft.twitchChatChannels.length}/{channelCap}). Use comma or space to separate, then add.</>}
              </p>
              {channelUpgrade !== null && (
                <p className='mt-1 text-[11px] font-medium text-brand'>
                  {planDisplayName(channelUpgrade)} allows{' '}
                  {channelUpgrade.limits.twitchChannelsPerOverlay ?? 'unlimited'} channels per overlay.
                </p>
              )}
            </div>
          </SectionCard>

          <SectionCard title='Style'>
            <label className='mb-4 block'>
              <FieldLabel>Animation style</FieldLabel>
              <select
                value={draft.style}
                onChange={(e) => setDraft((d) => ({ ...d, style: normalizeStyle(e.target.value) }))}
                className={inputClass}
              >
                <option value='RAIN'>Rain: emotes fall from the top</option>
                <option value='POP'>Pop: emotes appear and fade in place</option>
              </select>
            </label>

            <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
              <label className='block'>
                <FieldLabel>Emote size (px)</FieldLabel>
                <NumberInput
                  value={draft.emoteSize}
                  min={MIN_EMOTE_SIZE}
                  max={MAX_EMOTE_SIZE}
                  onCommit={commitInt('emoteSize')}
                  className={inputClass}
                />
              </label>
              <label className='block'>
                <FieldLabel>Max on screen</FieldLabel>
                <NumberInput
                  value={draft.density}
                  min={MIN_DENSITY}
                  max={MAX_DENSITY}
                  onCommit={commitInt('density')}
                  className={inputClass}
                />
              </label>
              <label className='block'>
                <FieldLabel>Lifetime (seconds)</FieldLabel>
                <NumberInput
                  value={draft.durationSeconds}
                  min={MIN_DURATION_SECONDS}
                  max={MAX_DURATION_SECONDS}
                  onCommit={commitInt('durationSeconds')}
                  className={inputClass}
                />
              </label>
            </div>
            <p className='mt-3 text-[11px] text-dm-text-accent'>
              Size is each emote's height; max-on-screen caps how many are
              airborne at once; lifetime is how long each one lives.
            </p>

            <div className='mt-4 space-y-2'>
              <FieldLabel>Emote sources</FieldLabel>
              {providerToggles.map(({ field, label }) => (
                <label key={field} className='flex items-center gap-2 text-sm text-dm-text-primary'>
                  <input
                    type='checkbox'
                    checked={draft[field]}
                    onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.checked }))}
                    className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                  />
                  {label}
                </label>
              ))}
            </div>
          </SectionCard>
        </fieldset>
      </div>

      <div className='lg:col-span-1'>
        <div className='sticky top-4 space-y-3'>
          <div className='flex items-center gap-2'>
            <button
              type='submit'
              disabled={submitting || !draftValid}
              className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {submitting && <LoadingSpinner />}
              {isEdit ? 'Save changes' : 'Create emote wall'}
            </button>
            <button
              type='button'
              onClick={onCancel}
              disabled={submitting}
              className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
            >
              Cancel
            </button>
          </div>
          {error !== null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          {savedNote && <SavedNote testId='emote-wall-saved' />}

          {isEdit && (
            <>
              <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                Browser source URL
              </h4>
              <OverlayLinkBlock link={overlayLink} label='emote wall' />
            </>
          )}
          {!isEdit && (
            <p className='text-[11px] text-dm-text-accent'>
              The browser-source link appears here (and in the list) after the
              first save.
            </p>
          )}

          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Preview
          </h4>

          <div className='inline-flex rounded-md border border-white/10 p-0.5' role='group' aria-label='Animation style'>
            {WALL_STYLE_TOGGLES.map(({ style, label, testId }) => (
              <button
                key={style}
                type='button'
                onClick={() => setDraft((d) => (d.style === style ? d : { ...d, style }))}
                aria-pressed={draft.style === style}
                data-testid={testId}
                className={draft.style === style
                  ? 'rounded bg-white/10 px-3 py-1 text-xs font-semibold text-dm-text-primary ring-1 ring-inset ring-white/20'
                  : 'rounded px-3 py-1 text-xs font-medium text-dm-text-secondary hover:text-dm-text-primary'}
              >
                {label}
              </button>
            ))}
          </div>
          <div className='overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-600'>
            <EmoteWallMockup config={draft} />
          </div>
          <p className='text-[11px] text-dm-text-accent'>
            Sample emotes at a 1920 x 1080 source, from this wall's own emote
            sources. The live overlay uses emotes from chat in the selected
            channels.
          </p>
          {previewReducedMotion && (
            <p className='text-[11px] text-dm-text-accent' data-testid='wall-preview-reduced-motion'>
              Your system asks for reduced motion, so the sample emotes hold
              still here and Rain and Pop look alike. The live overlay still
              animates.
            </p>
          )}
        </div>
      </div>
    </form>
  )
}

const WallListRow = ({
  wall,
  onEdit,
  onDuplicate,
  onDelete,
  busyAction,
  atCap
}: {
  wall: EmoteWallSummary
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  busyAction: null | 'delete' | 'duplicate'
  atCap: boolean
}): JSX.Element => {
  const busy = busyAction != null
  const overlayLink = useAccountOverlayLink('emotes', wall.emoteWallId)
  return (
    <li className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className='shrink-0 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
            Emote wall
          </span>
          <div className='flex min-w-0 flex-col leading-tight'>
            <span className='truncate text-sm font-medium text-dm-text-primary'>
              {wall.name}
            </span>
            <span className='truncate text-[11px] text-dm-text-accent'>
              {wall.twitchChatChannels.length} channel{wall.twitchChatChannels.length === 1 ? '' : 's'} · {wall.twitchChatChannels.join(', ')} · {styleLabel(wall.style)} · {wall.emoteSize}px
            </span>
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          <OverlayLinkRowActions link={overlayLink} label={`${wall.name} emote wall`} />
          <button
            type='button'
            onClick={onEdit}
            title='Edit'
            disabled={busy}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            <PencilSquareIcon className='size-3.5' aria-hidden='true' />
            Edit
          </button>
          <button
            type='button'
            onClick={onDuplicate}
            title={atCap ? "Your account's emote-wall limit is reached" : 'Duplicate'}
            disabled={busy || atCap}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'duplicate' ? <LoadingSpinner /> : <DocumentDuplicateIcon className='size-3.5' aria-hidden='true' />}
            Duplicate
          </button>
          <button
            type='button'
            onClick={onDelete}
            disabled={busy}
            title='Delete'
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'delete' ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
            Delete
          </button>
        </div>
      </div>
    </li>
  )
}

export const EmoteWallManager = ({
  view,
  basePath,
  backLabel
}: {
  view: QuickOverlayManagerView
  basePath: string
  backLabel: string
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const navigate = useNavigate()

  const [walls, setWalls] = useState<EmoteWallSummary[] | null>(null)
  const [listBusy, setListBusy] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const loadWalls = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    setListBusy(true)
    setListError(null)
    try {
      const { walls: fetched } = await graphqlRequest<{ walls: EmoteWallSummary[] }>(
        listMyEmoteWallsQuery, {}, auth0Token
      )
      setWalls(fetched)
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load emote walls')
    } finally {
      setListBusy(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void loadWalls()
  }, [loadWalls])

  const { enforcedLimitFor, upgradeForLimit } = useEntitlements()
  const wallCap = enforcedLimitFor('emoteWallsPerAccount')
  const wallCount = walls === null ? 0 : walls.length
  const atWallCap = wallCap !== null && wallCount >= wallCap
  const wallUpgrade = atWallCap
    ? upgradeForLimit('emoteWallsPerAccount', wallCap)
    : null

  const [rowBusy, setRowBusy] = useState<{ id: string, action: 'delete' | 'duplicate' } | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const goList = (): void => { void navigate(basePath) }
  const goCreate = (): void => { void navigate(`${basePath}/new`) }
  const goEdit = (emoteWallId: string): void => { void navigate(`${basePath}/${emoteWallId}`) }

  const editingId = typeof view === 'object' ? view.edit : null
  const editing = useMemo<EmoteWallSummary | null>(() => {
    if (editingId === null || walls === null) return null
    return walls.find((w) => w.emoteWallId === editingId) ?? null
  }, [editingId, walls])
  const editLink = useAccountOverlayLink('emotes', editing === null ? null : editing.emoteWallId)

  const upsertWall = (wall: EmoteWallSummary): void => {
    setWalls((prev) => {
      if (prev === null) return [wall]
      const exists = prev.some((w) => w.emoteWallId === wall.emoteWallId)
      return exists
        ? prev.map((w) => w.emoteWallId === wall.emoteWallId ? wall : w)
        : [...prev, wall]
    })
  }

  const removeWall = (emoteWallId: string): void => {
    setWalls((prev) => prev === null ? prev : prev.filter((w) => w.emoteWallId !== emoteWallId))
  }

  const handleDelete = async (wall: EmoteWallSummary): Promise<void> => {
    if (auth0Token == null || rowBusy != null) return
    const ok = globalThis.confirm(
      `Delete emote wall "${wall.name}"? Any browser source still pointing at its URL goes permanently blank.`
    )
    if (!ok) return
    setRowBusy({ id: wall.emoteWallId, action: 'delete' })
    setRowError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteEmoteWallMutation, { emoteWallId: wall.emoteWallId }, auth0Token
      )
      if (result.deleted) removeWall(wall.emoteWallId)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete emote wall')
    } finally {
      setRowBusy(null)
    }
  }

  const handleDuplicate = async (wall: EmoteWallSummary): Promise<void> => {
    if (auth0Token == null || rowBusy != null) return
    setRowBusy({ id: wall.emoteWallId, action: 'duplicate' })
    setRowError(null)
    try {
      const { wall: created } = await graphqlRequest<{ wall: EmoteWallSummary | null }>(
        duplicateEmoteWallMutation, { emoteWallId: wall.emoteWallId }, auth0Token
      )
      if (created == null) {
        setRowError('Failed to duplicate emote wall')
        return
      }
      upsertWall(created)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to duplicate emote wall')
    } finally {
      setRowBusy(null)
    }
  }

  if (view === 'create') {
    return (
      <EmoteWallForm
        existing={null}
        auth0Token={auth0Token}
        overlayLink={editLink}
        onSaved={(wall) => {
          upsertWall(wall)
          void navigate(`${basePath}/${wall.emoteWallId}`, { replace: true })
        }}
        onCancel={goList}
        backLabel={backLabel}
      />
    )
  }

  if (typeof view === 'object') {
    if (walls === null) {
      return (
        <div className='mt-4 flex items-center justify-center px-4 pb-4 py-10'>
          <LoadingSpinner />
        </div>
      )
    }
    if (editing === null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>That emote wall no longer exists.</ErrorInline>
          <button onClick={goList} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            {backLabel}
          </button>
        </div>
      )
    }
    return (
      <EmoteWallForm
        existing={editing}
        auth0Token={auth0Token}
        overlayLink={editLink}
        onSaved={upsertWall}
        onCancel={goList}
        backLabel={backLabel}
      />
    )
  }

  let listContent: JSX.Element
  if (walls === null) {
    listContent = (
      <div className='flex items-center justify-center rounded-lg border border-dashed border-white/10 px-4 py-10'>
        <LoadingSpinner />
      </div>
    )
  } else if (walls.length === 0) {
    listContent = (
      <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
        <FaceSmileIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
        <p>No emote walls yet.</p>
        <button
          type='button'
          onClick={goCreate}
          className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
        >
          <PlusIcon className='size-3.5' aria-hidden='true' />
          Create the first one
        </button>
      </div>
    )
  } else {
    listContent = (
      <ul className='space-y-2' data-testid='emote-walls-list'>
        {sortByName(walls, (w) => w.name, (w) => w.emoteWallId).map((wall) => (
          <WallListRow
            key={wall.emoteWallId}
            wall={wall}
            onEdit={() => goEdit(wall.emoteWallId)}
            onDuplicate={() => { void handleDuplicate(wall) }}
            onDelete={() => { void handleDelete(wall) }}
            busyAction={rowBusy?.id === wall.emoteWallId ? rowBusy.action : null}
            atCap={atWallCap}
          />
        ))}
      </ul>
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <SectionCard
        title='Emote Walls'
        action={
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => { void loadWalls() }}
              disabled={listBusy}
              title='Refresh'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {listBusy ? <LoadingSpinner /> : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
              Refresh
            </button>
            <button
              type='button'
              data-testid='add-emote-wall-button'
              onClick={goCreate}
              disabled={atWallCap}
              title={atWallCap ? "Your account's emote-wall limit is reached" : undefined}
              className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              <PlusIcon className='size-3.5' aria-hidden='true' />
              Add new emote wall
            </button>
          </div>
        }
      >
        {listError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setListError(null)}>{listError}</ErrorInline>
          </div>
        )}
        {rowError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}

        {atWallCap && (
          <div className='mb-3 rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-[11px] text-dm-text-secondary'>
            Your account has reached its emote-wall limit ({wallCap}).
            {wallUpgrade !== null && (
              <> <span className='font-medium text-brand'>{planDisplayName(wallUpgrade)} allows {wallUpgrade.limits.emoteWallsPerAccount ?? 'unlimited'} per account.</span></>
            )}
          </div>
        )}

        {listContent}

        <div className='mt-4 space-y-1 text-[11px] text-dm-text-accent'>
          <p>
            Each emote wall has its own browser-source URL. Add it to your streaming software with a transparent background.
          </p>
          <p>{OVERLAY_LINK_NOTE}</p>
        </div>
      </SectionCard>
    </div>
  )
}
